import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBalance, moveFunds } from '../utils/api';
import { usePlatform } from '../context/PlatformContext';

export default function MovePage() {
  useEffect(() => {
    document.title = 'Thunderbolt — Move Funds';
    return () => { document.title = 'Thunderbolt'; };
  }, []);

  const navigate = useNavigate();
  const platform = usePlatform();
  const [balance, setBalance]     = useState(null);
  const [direction, setDirection] = useState('spend_to_save');
  const [amount, setAmount]       = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState(null);

  useEffect(() => {
    getBalance().then(r => setBalance(r.data)).catch(console.error);
  }, []);

  const amountCents = Math.round(parseFloat(amount||0)*100);
  const feeCents    = Math.round(amountCents * (platform.feeBps / 10000));
  const netCents    = amountCents - feeCents;

  async function handleMove() {
    if (amountCents < platform.minSendCents) { setError(platform.minSendCents > 0 ? `Minimum is $${(platform.minSendCents/100).toFixed(2)}` : 'Please enter an amount'); return; }
    setError(''); setLoading(true);
    try {
      await moveFunds(direction==='spend_to_save' ? 'spend' : 'stack', amountCents);
      setSuccess({ direction, netCents });
    } catch (err) {
      setError(err.response?.data?.error || 'Move failed');
    } finally { setLoading(false); }
  }

  if (success) return (
    <div style={s.page}>
      <div style={s.successWrap}>
        <div style={s.successIcon}>✓</div>
        <div style={s.successTitle}>${(success.netCents/100).toFixed(2)} moved</div>
        <div style={s.successSub}>
          {success.direction==='spend_to_save'
            ? 'Queued for next BTC purchase'
            : 'Moved to Spend balance'}
        </div>
        <button style={s.btn} onClick={() => navigate('/dashboard')}>Done</button>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={() => navigate('/dashboard')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1D1D1F" strokeWidth="2">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <span style={s.headerTitle}>Move Funds</span>
        <div style={{width:36}}/>
      </div>

      <div style={s.body}>
        <div style={s.bucketsRow}>
          <div style={s.bucketCard}>
            <div style={s.bucketLabel}>Spend</div>
            <div style={s.bucketValue}>${balance?.spend?.usd||'0.00'}</div>
          </div>
          <div style={s.arrowWrap}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#AEAEB2" strokeWidth="2">
              <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/>
            </svg>
          </div>
          <div style={s.bucketCard}>
            <div style={s.bucketLabel}>Earning</div>
            <div style={{...s.bucketValue, color:'#007AFF'}}>${balance?.save?.usd||'0.00'}</div>
          </div>
        </div>

        <div style={s.sectionTitle}>Direction</div>

        <div style={s.dirRow}>
          {[
            {id:'spend_to_save', label:'Spend → Earning'},
            {id:'save_to_spend', label:'Earning → Spend'}
          ].map(d => (
            <button key={d.id}
              style={{...s.dirBtn, ...(direction===d.id ? s.dirBtnActive : {})}}
              onClick={() => setDirection(d.id)}>
              {d.label}
            </button>
          ))}
        </div>

        <div style={s.amountCard}>
          <label style={s.fieldLabel}>Amount</label>
          <div style={s.amountRow}>
            <span style={s.dollar}>$</span>
            <input style={s.amountInput} type="number" placeholder="0.00"
              min="1" step="0.01" value={amount}
              onChange={e => { setAmount(e.target.value); setError(''); }}/>
          </div>
        </div>

        {direction==='spend_to_save' && (
          <div style={s.notice}>Converted to BTC at next daily batch price.</div>
        )}

        <div style={s.summaryCard}>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>{`Service fee (${platform.feePct}%)`}</span>
            <span style={s.summaryValue}>${(feeCents/100).toFixed(2)}</span>
          </div>
          <div style={s.divider}/>
          <div style={s.summaryRow}>
            <span style={{...s.summaryLabel, fontWeight:600, color:'#1D1D1F'}}>Amount moved</span>
            <span style={{...s.summaryValue, fontWeight:600, color:'#1D1D1F'}}>${(netCents/100).toFixed(2)}</span>
          </div>
        </div>

        {error && <div style={s.error}>{error}</div>}

        <button style={s.btn} onClick={handleMove} disabled={loading}>
          {loading ? 'Moving...' : 'Move $'+(amountCents/100).toFixed(2)}
        </button>
      </div>
    </div>
  );
}

const s = {
  page:          {minHeight:'100vh', background:'#F5F5F7', paddingBottom:88},
  header:        {display:'flex', alignItems:'center', justifyContent:'space-between', padding:'56px 20px 16px'},
  back:          {width:36, height:36, borderRadius:10, background:'#fff', border:'none', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.08)'},
  headerTitle:   {fontSize:17, fontWeight:600, color:'#1D1D1F'},
  body:          {padding:'0 16px'},
  bucketsRow:    {display:'flex', alignItems:'center', gap:8, marginBottom:20},
  bucketCard:    {flex:1, background:'#fff', borderRadius:16, padding:'14px 16px', boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  bucketLabel:   {fontSize:12, color:'#6E6E73', marginBottom:4},
  bucketValue:   {fontSize:18, fontWeight:600},
  arrowWrap:     {display:'flex', alignItems:'center', justifyContent:'center'},
  sectionTitle:  {fontSize:12, fontWeight:600, color:'#AEAEB2', marginBottom:10, textTransform:'uppercase', letterSpacing:0.8},
  dirRow:        {display:'flex', gap:8, marginBottom:12},
  dirBtn:        {flex:1, padding:'11px 0', background:'#fff', border:'1.5px solid #F0F0F0', borderRadius:12, fontSize:13, fontWeight:500, color:'#6E6E73'},
  dirBtnActive:  {borderColor:'#007AFF', color:'#007AFF', background:'#EBF4FF'},
  amountCard:    {background:'#fff', borderRadius:20, padding:20, marginBottom:12, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  fieldLabel:    {fontSize:12, fontWeight:500, color:'#6E6E73', display:'block', marginBottom:8},
  amountRow:     {display:'flex', alignItems:'center', gap:4},
  dollar:        {fontSize:36, fontWeight:600},
  amountInput:   {fontSize:36, fontWeight:600, border:'none', background:'transparent', width:'100%', outline:'none'},
  notice:        {display:'none',background:'#FFF9EC', border:'1px solid #FFE8A3', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#B87A00', marginBottom:12},
  summaryCard:   {background:'#fff', borderRadius:16, padding:16, marginBottom:16, boxShadow:'0 2px 20px rgba(0,0,0,0.06)'},
  summaryRow:    {display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0'},
  summaryLabel:  {fontSize:14, color:'#6E6E73'},
  summaryValue:  {fontSize:14, color:'#6E6E73'},
  divider:       {height:1, background:'#F5F5F7', margin:'10px 0'},
  error:         {background:'#FFF0EF', border:'1px solid #FFD0CE', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#FF3B30', marginBottom:12},
  btn:           {width:'100%', padding:16, background:'#007AFF', color:'#fff', border:'none', borderRadius:14, fontSize:16, fontWeight:600, letterSpacing:-0.3},
  successWrap:   {display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', padding:24, gap:12, textAlign:'center'},
  successIcon:   {width:64, height:64, borderRadius:'50%', background:'#E6FAF5', color:'#00C48C', display:'flex', alignItems:'center', justifyContent:'center', fontSize:28},
  successTitle:  {fontSize:24, fontWeight:600},
  successSub:    {fontSize:14, color:'#6E6E73', lineHeight:1.5}
};
